import { MultiDepthTab } from "@/shared/lib/navigation/navigation.types"
import { cn, useDeviceType } from "@beringlab/ui"
import { ReactNode } from "@tanstack/react-router"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import FeedbackDialog from "./feedback-dialog.ui"
import Footer from "./footer"
import GlobalNavBar from "./global-nav-bar"
import Sidebar from "./side-bar"

export default function PageLayout({
  title,
  children,
  hasFooter = false,
  maxWidth,
  enableChangePageTitle = false,
  className,
  hideNav = false,
  sidebarList,
  tabs,
  hideProfile = false,
  isApiMode = false,
  handleApiButton,
  showPreviousButton = false,
  showFloatingButton = false,
  hideNavigationTab = false,
  background,
  styling,
}: Readonly<{
  title: string
  children: React.ReactNode
  hasFooter?: boolean
  sidebarList?: MultiDepthTab[]
  maxWidth: string
  enableChangePageTitle?: boolean
  className?: string
  hideNav?: boolean
  tabs?: ReactNode
  hideProfile?: boolean
  isApiMode?: boolean
  handleApiButton?: () => void
  showPreviousButton?: boolean
  showFloatingButton?: boolean
  hideNavigationTab?: boolean
  background?: string
  styling?: string
}>) {
  const { t } = useTranslation()
  const { isMobile, isDesktop, isTablet } = useDeviceType()

  // `maxWidth` is a bare pixel count for almost every page. The assistant transcript wants the
  // whole row instead, and `"full"` interpolated into `${maxWidth}px` is `"fullpx"` — not a
  // length. It rendered correctly only because the CSSOM discards a declaration it cannot parse,
  // so the intended layout was an accident that any later normalisation of this prop would undo.
  const maxWidthStyle = maxWidth === "full" ? "100%" : `${maxWidth}px`

  useEffect(() => {
    if (!enableChangePageTitle) {
      document.title = `${title} | Bering AI`
    }
  }, [enableChangePageTitle, title])

  return (
    <div
      className="min-h-[100vh] min-w-0 bg-gray-backdrop text-foreground"
      style={{ background }}
    >
      {!hideNav ? (
        <GlobalNavBar
          hideProfile={hideProfile}
          showPreviousButton={showPreviousButton}
          isApiMode={isApiMode}
          handleApiButton={handleApiButton}
          hideNavigationTab={hideNavigationTab}
        />
      ) : null}
      {tabs && (
        <section
          className="w-full min-w-0 border-b border-border-color"
          style={{ background }}
        >
          <div
            className="mx-auto px-3"
            style={{
              maxWidth: maxWidthStyle,
            }}
          >
            {tabs}
          </div>
        </section>
      )}
      <div className="flex flex-col">
        <div
          className={cn("flex", sidebarList ? "" : styling || "pt-6")}
          style={{
            minHeight: `calc(100vh - ${
              (hasFooter ? 90 : 50) + (tabs ? 49 : 0)
            }px)`,
          }}
        >
          {sidebarList ? (
            <Sidebar list={sidebarList} title={t("settings.setting")} />
          ) : null}
          <main
            className={cn(
              "mx-auto min-w-0 flex-grow px-3 md:min-w-[auto]",
              className
            )}
            style={{
              maxWidth: maxWidthStyle,
            }}
          >
            {children}
          </main>
        </div>
        {hasFooter && !isMobile ? <Footer /> : <section />}
      </div>
      {(isDesktop || isTablet) && showFloatingButton && <FeedbackDialog />}
    </div>
  )
}
